import { createContext, useContext, useCallback, useEffect, useRef, useState, type FC, type ReactNode } from "react";
import { decodeJwt } from "jose";

import { clearTokens, getStoredTokens, persistTokens } from "../../services/token-manager";
import { claimOAuthCallback, isValidOAuthCallbackState } from "./oauth-callback";

export type Client = {
  id: string;
  name: string;
  applicationId: string;
  tenantId: string;
};

type AuthUser = {
  id: string;
  email: string;
  name: string;
};

type AuthState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null;
  clientId: string | null;
  error: string | null;
};

type AuthContextType = AuthState & {
  clients: Client[];
  login: (clientId?: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

const FUSIONAUTH_URL = import.meta.env.VITE_FUSIONAUTH_URL ?? "https://bloodline-iam.eidra.com";
const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI ?? `${window.location.origin}/auth/callback`;
const POST_LOGOUT_URI: string | undefined = import.meta.env.VITE_POST_LOGOUT_URI ?? undefined;

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? "https://bloodline-api.eidra.com";

const STATIC_CLIENTS: Client[] = (() => {
  try {
    const env = import.meta.env.VITE_CLIENTS;
    if (!env) return [];
    const parsed = JSON.parse(env);
    return (parsed?.clients ?? []) as Client[];
  } catch {
    return [];
  }
})();

const fetchActiveClients = async (): Promise<Client[]> => {
  const response = await fetch(`${BACKEND_URL}/v3/cli/clients/active`);
  if (!response.ok) throw new Error(`Failed to fetch clients: ${String(response.status)}`);
  const data = (await response.json()) as { name: string; clientId: string; tenantId: string }[];
  return data
    .filter((c) => c.clientId)
    .map((c) => ({ id: c.clientId, name: c.name, applicationId: c.clientId, tenantId: c.tenantId }));
};

const STORAGE_KEY = "bloodline.auth";
const PKCE_VERIFIER_KEY = "bloodline.auth.pkce_verifier";
const AUTH_STATE_KEY = "bloodline.auth.state";
const PENDING_APP_ID_KEY = "bloodline.auth.pending_app_id";

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(36).padStart(2, "0"))
    .join("")
    .slice(0, length);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function isValidAuthState(data: unknown): data is Partial<AuthState> {
  if (!data || typeof data !== "object") return false;
  const obj = data as Record<string, unknown>;

  // Validate identity fields
  if (obj["user"] !== null && obj["user"] !== undefined && typeof obj["user"] !== "object") return false;
  if (obj["clientId"] !== null && obj["clientId"] !== undefined && typeof obj["clientId"] !== "string") return false;
  if (obj["tokenExpiresAt"] !== undefined) {
    const exp = obj["tokenExpiresAt"];
    if (typeof exp !== "number" || !isFinite(exp) || exp < 0) return false;
  }

  return true;
}

function getStoredAuth(): Partial<AuthState> | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);

    // Validate the parsed data before returning
    if (!isValidAuthState(parsed)) {
      console.warn("Stored auth state is invalid or corrupted, discarding...");
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn("Failed to parse stored auth state:", error);
    // Clear corrupted data to prevent repeated failures
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function storeAuth(state: Partial<AuthState>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

/**
 * The stored state is only a cache: it is thrown away whenever validation rejects it,
 * and a release older than the client selection never wrote one. The token carries the
 * client the login flow itself read, so it stays the authority when restoring a
 * session. Without this the app comes back signed in but with no client, and every
 * page that needs one renders nothing at all.
 */
function clientIdFromToken(accessToken: string | null | undefined): string | null {
  if (!accessToken) return null;

  try {
    return (decodeJwt(accessToken)["tid"] as string | undefined) ?? null;
  } catch {
    return null;
  }
}

function clearStoredAuth() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(PKCE_VERIFIER_KEY);
  localStorage.removeItem(AUTH_STATE_KEY);
  clearTokens();
}

function clearPendingAuth() {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(AUTH_STATE_KEY);
  sessionStorage.removeItem(PENDING_APP_ID_KEY);
}

function getDiscoveryUrl(tenantId?: string): string {
  const base = `${FUSIONAUTH_URL}/.well-known/openid-configuration`;
  return tenantId ? `${base}?tenantId=${tenantId}` : base;
}

async function getTokenEndpoint(tenantId?: string): Promise<string> {
  try {
    const response = await fetch(getDiscoveryUrl(tenantId));
    const config = await response.json();
    return config.token_endpoint;
  } catch {
    return `${FUSIONAUTH_URL}/oauth2/token`;
  }
}

async function exchangeCodeForTokens(code: string, codeVerifier: string, clientId: string) {
  const tokenEndpoint = await getTokenEndpoint();
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: REDIRECT_URI,
    client_id: clientId,
    code_verifier: codeVerifier,
  });

  const response = await fetch(tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    console.error("Token exchange error", response.status, body);
    throw new Error("Authentication failed");
  }

  return response.json();
}

async function refreshAccessToken(refreshToken: string, clientId: string) {
  const tokenEndpoint = await getTokenEndpoint();
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: clientId,
  });

  const response = await fetch(tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    console.error("Token refresh error", response.status, body);
    throw new Error("Token refresh failed");
  }

  const payload = (await response.json()) as Record<string, unknown>;
  return {
    ...payload,
    refresh_token: typeof payload["refresh_token"] === "string" ? payload["refresh_token"] : refreshToken,
  };
}

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [clients, setClients] = useState<Client[]>(STATIC_CLIENTS);
  const oauthCallbackClaim = useRef(false);

  useEffect(() => {
    fetchActiveClients()
      .then((fetched) => {
        if (fetched.length > 0) setClients(fetched);
      })
      .catch((err: unknown) => {
        console.warn("Could not fetch active clients from API, falling back to static config", err);
      });
  }, []);

  const [state, setState] = useState<AuthState>(() => {
    try {
      const stored = getStoredAuth();
      const storedTokens = getStoredTokens();
      if (
        storedTokens?.accessToken &&
        typeof storedTokens.expiresAt === "number" &&
        isFinite(storedTokens.expiresAt) &&
        storedTokens.expiresAt > Date.now()
      ) {
        const decoded = decodeJwt(storedTokens.accessToken);
        return {
          isAuthenticated: true,
          isLoading: false,
          user: {
            id: decoded.sub ?? "",
            email: (decoded["email"] as string | undefined) ?? "",
            name:
              (decoded["preferred_username"] as string | undefined) ?? (decoded["email"] as string | undefined) ?? "",
          },
          accessToken: storedTokens.accessToken ?? null,
          refreshToken: storedTokens.refreshToken ?? null,
          tokenExpiresAt: storedTokens.expiresAt ?? null,
          clientId: stored?.["clientId"] ?? clientIdFromToken(storedTokens.accessToken),
          error: null,
        };
      }

      if (storedTokens?.refreshToken) {
        return {
          isAuthenticated: false,
          isLoading: true,
          user: (stored?.["user"] as AuthUser | null) ?? null,
          accessToken: storedTokens.accessToken ?? null,
          refreshToken: storedTokens.refreshToken,
          tokenExpiresAt: storedTokens.expiresAt ?? null,
          clientId: stored?.["clientId"] ?? clientIdFromToken(storedTokens.accessToken),
          error: null,
        };
      }
    } catch (error) {
      console.error("Error initializing auth from stored state:", error);
      // Fall through to default state
    }
    // Only show loading if we're on the auth callback page (waiting for token exchange)
    const isCallbackPage = window.location.pathname.includes("/auth/callback");
    return {
      isAuthenticated: false,
      isLoading: isCallbackPage,
      user: null,
      accessToken: null,
      refreshToken: null,
      tokenExpiresAt: null,
      clientId: null,
      error: null,
    };
  });

  const processTokenResponse = useCallback((tokenData: Record<string, unknown>, clientAppId: string) => {
    const accessToken = tokenData["access_token"] as string;
    const refreshToken = tokenData["refresh_token"] as string | undefined;
    const decoded = decodeJwt(accessToken);
    const expiresAt = typeof decoded.exp === "number" ? decoded.exp * 1000 : Date.now() + 3600_000;

    const newState: AuthState = {
      isAuthenticated: true,
      isLoading: false,
      user: {
        id: decoded.sub ?? "",
        email: (decoded["email"] as string | undefined) ?? "",
        name: (decoded["preferred_username"] as string | undefined) ?? (decoded["email"] as string | undefined) ?? "",
      },
      accessToken,
      refreshToken: refreshToken ?? null,
      tokenExpiresAt: expiresAt,
      clientId: (decoded["tid"] as string | undefined) ?? null,
      error: null,
    };

    setState(newState);
    storeAuth({ user: newState.user, clientId: newState.clientId, tokenExpiresAt: expiresAt });
    persistTokens({
      accessToken,
      ...(refreshToken ? { refreshToken } : {}),
      expiresAt,
    });

    // Store the applicationId used for this session
    localStorage.setItem("bloodline.auth.appId", clientAppId);
  }, []);

  // Handle OAuth callback
  useEffect(() => {
    const handleCallback = async () => {
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");
      const returnedState = url.searchParams.get("state");

      if (!code || !claimOAuthCallback(oauthCallbackClaim, url.pathname, code)) return;

      const storedVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
      const storedState = sessionStorage.getItem(AUTH_STATE_KEY);
      const storedAppId = sessionStorage.getItem(PENDING_APP_ID_KEY);

      if (!storedVerifier || !storedAppId) {
        clearPendingAuth();
        setState((prev) => ({ ...prev, isLoading: false, error: "Missing PKCE verifier" }));
        return;
      }

      if (!isValidOAuthCallbackState(returnedState, storedState)) {
        clearPendingAuth();
        setState((prev) => ({ ...prev, isLoading: false, error: "Invalid authentication state" }));
        return;
      }

      // Consume the OAuth binding before the asynchronous exchange so a
      // duplicate callback cannot reuse it while the first request is pending.
      clearPendingAuth();

      try {
        const tokenData = await exchangeCodeForTokens(code, storedVerifier, storedAppId);
        processTokenResponse(tokenData, storedAppId);
      } catch (err) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: err instanceof Error ? err.message : "Authentication failed",
        }));
      }
    };

    void handleCallback();
  }, [processTokenResponse]);

  // Token refresh
  useEffect(() => {
    if (!state.accessToken || !state.refreshToken || !state.tokenExpiresAt) return;

    const refreshBuffer = 60_000; // 1 minute before expiry
    const timeUntilRefresh = state.tokenExpiresAt - Date.now() - refreshBuffer;

    if (timeUntilRefresh <= 0) {
      // Refresh immediately
      const appId = localStorage.getItem("bloodline.auth.appId") ?? "";
      refreshAccessToken(state.refreshToken, appId)
        .then((tokenData) => {
          processTokenResponse(tokenData, appId);
        })
        .catch(() => {
          clearStoredAuth();
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            tokenExpiresAt: null,
            clientId: null,
            error: "Session expired",
          });
        });
      return;
    }

    const timer = setTimeout(() => {
      const appId = localStorage.getItem("bloodline.auth.appId") ?? "";
      if (state.refreshToken) {
        refreshAccessToken(state.refreshToken, appId)
          .then((tokenData) => {
            processTokenResponse(tokenData, appId);
          })
          .catch(() => {
            clearStoredAuth();
            setState({
              isAuthenticated: false,
              isLoading: false,
              user: null,
              accessToken: null,
              refreshToken: null,
              tokenExpiresAt: null,
              clientId: null,
              error: "Session expired",
            });
          });
      }
    }, timeUntilRefresh);

    return () => {
      clearTimeout(timer);
    };
  }, [state.accessToken, state.refreshToken, state.tokenExpiresAt, processTokenResponse]);

  // Restore an expired access-token session from the longer-lived refresh token.
  useEffect(() => {
    if (window.location.pathname.includes("/auth/callback") || state.isAuthenticated || !state.isLoading) {
      return;
    }

    const storedTokens = getStoredTokens();
    if (!storedTokens?.refreshToken) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    const appId = localStorage.getItem("bloodline.auth.appId") ?? "";
    if (!appId) {
      clearStoredAuth();
      setState((prev) => ({ ...prev, isLoading: false, error: "Session expired" }));
      return;
    }

    let cancelled = false;
    refreshAccessToken(storedTokens.refreshToken, appId)
      .then((tokenData) => {
        if (!cancelled) {
          processTokenResponse(tokenData, appId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          clearStoredAuth();
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            tokenExpiresAt: null,
            clientId: null,
            error: "Session expired",
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [state.isLoading, state.isAuthenticated, processTokenResponse]);

  const login = useCallback(
    async (preferredClientId?: string) => {
      const client = preferredClientId
        ? clients.find((c) => c.id === preferredClientId)
        : clients.length === 1
          ? clients[0]
          : undefined;

      if (!client) {
        console.error(
          preferredClientId
            ? `No FusionAuth client found for id "${preferredClientId}"`
            : "login() called without a clientId but multiple clients are configured — pass an explicit clientId",
        );
        return;
      }

      const codeVerifier = generateRandomString(64);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const authState = generateRandomString(32);

      sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
      sessionStorage.setItem(AUTH_STATE_KEY, authState);
      sessionStorage.setItem(PENDING_APP_ID_KEY, client.applicationId);

      const params = new URLSearchParams({
        client_id: client.applicationId,
        redirect_uri: REDIRECT_URI,
        response_type: "code",
        scope: "openid email profile offline_access",
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        state: authState,
        tenantId: client.tenantId,
      });

      window.location.href = `${FUSIONAUTH_URL}/oauth2/authorize?${params.toString()}`;
    },
    [clients],
  );

  const logout = useCallback(() => {
    const appId = localStorage.getItem("bloodline.auth.appId");
    clearStoredAuth();
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      accessToken: null,
      refreshToken: null,
      tokenExpiresAt: null,
      clientId: null,
      error: null,
    });

    if (appId) {
      const params = new URLSearchParams({ client_id: appId });
      if (POST_LOGOUT_URI) params.set("post_logout_redirect_uri", POST_LOGOUT_URI);
      window.location.href = `${FUSIONAUTH_URL}/oauth2/logout?${params.toString()}`;
    }
  }, []);

  return <AuthContext.Provider value={{ ...state, clients, login, logout }}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthProvider;
